import { Anchor } from '@/components/ui/Anchor'
import { Button } from '@/components/ui/button'
import { Reveal } from './Reveal'

export interface BookkeeperPricingProps {
  /** Signup destination for the priced tiers. */
  ctaHref: string
  onCtaClick?: () => void
  /** Fires on the 10+ tier's email link, which is a different intent from a signup. */
  onContactClick?: () => void
}

/** Pre-written so a 10+ fleet doesn't have to compose the first message themselves. */
const CONTACT_HREF =
  'mailto:bookkeeper@cai.ai' +
  `?subject=${encodeURIComponent('Cai Bookkeeper pricing')}` +
  `&body=${encodeURIComponent("Hi there! I'm interested in Cai Bookkeeper for my fleet of 10+ trucks.")}`

const TIERS = [
  { fleet: '1–5 trucks', bookkeeper: '$199', bundle: '$300' },
  { fleet: '6–10 trucks', bookkeeper: '$249', bundle: '$400' },
]

export function BookkeeperPricing({ ctaHref, onCtaClick, onContactClick }: BookkeeperPricingProps) {
  return (
    <section id="pricing" className="mx-auto flex max-w-(--maxw) flex-col gap-12 px-5.5 py-24 lg:py-32">
      <Reveal className="flex flex-col gap-4">
        <span className="text-brand-800 font-mono text-xs font-bold tracking-[0.14em] uppercase">
          Pricing
        </span>
        <h2 className="h2 text-pretty">Clear pricing, no hidden fees</h2>
        <p className="text-md text-ink-600 max-w-140 leading-[1.55]">
          Books closed within 5 days and a live bookkeeper, on every plan. Add{' '}
          <Anchor href="/cfo" className="underline">
            Cai AI CFO
          </Anchor>{' '}
          to bundle and save.
        </p>
      </Reveal>

      <div className="grid grid-cols-3 gap-4 max-lg:grid-cols-1">
        {TIERS.map((tier, i) => (
          <Reveal key={tier.fleet} delay={i * 100}>
            <div className="border-ink-100 flex h-full flex-col gap-6 rounded-sm border p-8">
              <span className="text-ink-600 font-mono text-xs font-bold tracking-[0.14em] uppercase">
                {tier.fleet}
              </span>
              <div className="flex flex-col gap-1">
                <span className="text-ink-600 text-sm font-semibold">Bookkeeper</span>
                <span className="flex items-baseline gap-1.5">
                  <span className="font-mono text-4xl font-bold tracking-[-0.02em]">{tier.bookkeeper}</span>
                  <span className="text-ink-600 text-md">/month</span>
                </span>
              </div>
              <div className="border-ink-100 flex flex-col gap-1 border-t pt-6">
                <span className="text-ink-600 text-sm font-semibold">Bookkeeper + CFO</span>
                <span className="flex items-baseline gap-1.5">
                  <span className="font-mono text-4xl font-bold tracking-[-0.02em]">{tier.bundle}</span>
                  <span className="text-ink-600 text-md">/month</span>
                </span>
                <Anchor
                  href="/cfo"
                  className="text-ink-600 hover:text-brand-800 mt-1 self-start text-sm underline"
                >
                  What&apos;s Cai AI CFO?
                </Anchor>
              </div>
              <div className="mt-auto pt-2">
                <Button size="lg" href={ctaHref} onClick={onCtaClick} className="w-full">
                  Create your account
                </Button>
              </div>
            </div>
          </Reveal>
        ))}

        <Reveal delay={200}>
          <div className="border-ink-100 flex h-full flex-col gap-6 rounded-sm border p-8">
            <span className="text-ink-600 font-mono text-xs font-bold tracking-[0.14em] uppercase">
              10+ trucks
            </span>
            <span className="text-3xl font-bold tracking-[-0.02em]">Custom pricing</span>
            <p className="text-md text-ink-600 leading-[1.55]">
              Running a bigger operation? Tell us about your fleet and we&apos;ll put together a price that
              fits.
            </p>
            <div className="mt-auto pt-2">
              <Button
                variant="outline"
                size="lg"
                href={CONTACT_HREF}
                onClick={onContactClick}
                className="w-full"
              >
                Contact us
              </Button>
            </div>
          </div>
        </Reveal>
      </div>

      <Reveal>
        <div className="bg-brand-100 border-brand-300 flex flex-col gap-2 rounded-sm border px-8 py-7">
          <span className="text-brand-800 font-mono text-xs font-bold tracking-[0.14em] uppercase">
            Intro offer · Limited time
          </span>
          <p className="text-2xl font-bold tracking-[-0.01em] text-pretty">
            Your first 3 months are free, on any plan.
          </p>
        </div>
      </Reveal>
    </section>
  )
}
